/**
 * Pure fetch helper extracted from `useGetSpaceConversations` so the spaces
 * sweet-state store can call it from inside an action without depending on
 * React or on the hook itself.
 */
import type { ConversationChannel } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { CHAT_STORE_STATUS } from '@atlassian/conversation-assistant-store/controllers/chat/types';
import { performHttpFetch } from '@atlassian/conversation-assistant-utils/httpUtils';

import type { SpaceConversationsResponse } from '../../controllers/space-store/types';
import type { RovoNavigationConversation as Conversation } from '../../types/rovo-navigation-conversation';

const SPACES_API_PATH = '/gateway/api/assist/rovo/v1/spaces';

export type GetSpaceConversationsFetchParams = {
	spaceId: string;
	cloudId: string;
	product: string;
	experienceId: string;
	offset?: number;
	count?: number;
};

/**
 * List endpoint returns {@link ConversationChannel} JSON (snake_case). Navigation UI
 * expects store-shaped {@link Conversation} (camelCase), e.g. `lastMessageTimestamp`.
 */
type ApiSpaceConversationRow = Omit<ConversationChannel, 'status'> & {
	status?: ConversationChannel['status'] | null;
	read_status?: boolean | null;
	is_pinned?: boolean | null;
	pinned_at?: string | null;
};

const mapSpaceConversationFromApi = (
	channel: ApiSpaceConversationRow,
	spaceId: string,
): Conversation => {
	const lastMessage = channel.last_message ?? undefined;
	return {
		id: channel.id,
		name: channel.name ?? '',
		messages: [],
		status: CHAT_STORE_STATUS.idle,
		traces: [],
		timeCreated: channel.time_created,
		lastMessage,
		lastMessageType: channel.last_message_type ?? [],
		lastMessageTimestamp: channel.last_message_timestamp,
		lastAgentMessageStatus: channel.last_agent_message_status,
		readStatus: channel.readStatus ?? channel.read_status ?? null,
		agent: channel.agent,
		scopes: channel.scopes,
		tags: channel.tags,
		rovoSpaceId: spaceId,
		isEmpty: !lastMessage?.trim(),
		isPinned: channel.isPinned ?? channel.is_pinned ?? undefined,
		pinnedAt: channel.pinnedAt ?? channel.pinned_at ?? null,
	};
};

export const getSpaceConversationsFetchAction = async ({
	spaceId,
	cloudId,
	product,
	experienceId,
	offset,
	count,
}: GetSpaceConversationsFetchParams): Promise<SpaceConversationsResponse> => {
	const params = new URLSearchParams();
	if (offset !== undefined) {
		params.set('offset', String(offset));
	}
	if (count !== undefined) {
		params.set('count', String(count));
	}
	const query = params.toString();
	const raw = (await performHttpFetch({
		path: `${SPACES_API_PATH}/${spaceId}/conversations${query ? `?${query}` : ''}`,
		options: {
			method: 'GET',
			headers: {
				'Content-Type': 'application/json',
				'x-cloudid': cloudId,
				'x-product': product,
				'x-experience-id': experienceId,
			},
			credentials: 'include',
		},
	})) as { conversations: ApiSpaceConversationRow[]; offset: number; totalCount: number };

	return {
		...raw,
		conversations: raw.conversations.map((row) => mapSpaceConversationFromApi(row, spaceId)),
		requestedCount: count,
	};
};
